import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { supabase } from '../supabase/config';

const AuthContext = createContext({});

// Get Supabase URL and key for direct REST API calls
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Backoff between attempts to load app access. A failed lookup (network blip, 5xx) must
// not be mistaken for "this user has no access", so we retry before giving up and then
// surface a retryable error instead of Access Denied.
const APP_ACCESS_RETRY_DELAYS_MS = [1000, 2000, 4000];

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Look up whether the user may use the dispatch app. Throws on network/HTTP failure;
// returns the app list when the lookup itself succeeded.
const lookupUserApps = async (userId, accessToken) => {
  // Check dispatch.users table for active user
  const response = await fetch(
    `${supabaseUrl}/rest/v1/users?select=id,is_active&id=eq.${userId}`,
    {
      headers: {
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${accessToken}`,
        'Accept-Profile': 'dispatch'  // Use dispatch schema
      }
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Access lookup failed (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  // If user exists and is_active, grant dispatch access
  return data.length > 0 && data[0].is_active ? ['dispatch'] : [];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userApps, setUserApps] = useState([]);
  // Set when app access could not be determined (as opposed to the user having none)
  const [appsError, setAppsError] = useState(null);
  const mountedRef = useRef(true);
  // The session whose access we last tried to load, so the Retry button can re-run it
  const sessionRef = useRef(null);

  const fetchUserApps = useCallback(async (userId, accessToken) => {
    for (let attempt = 0; ; attempt++) {
      try {
        const apps = await lookupUserApps(userId, accessToken);
        if (!mountedRef.current) return;
        setUserApps(apps);
        setAppsError(null);
        return;
      } catch (err) {
        console.error('Error fetching user access:', err);
        if (attempt >= APP_ACCESS_RETRY_DELAYS_MS.length) {
          if (!mountedRef.current) return;
          setUserApps([]);
          setAppsError('Could not check your access. Please check your connection and try again.');
          return;
        }
        await sleep(APP_ACCESS_RETRY_DELAYS_MS[attempt]);
        if (!mountedRef.current) return;
      }
    }
  }, []);

  // Re-run the access lookup after a failure (used by the retry screen)
  const retryAppAccess = useCallback(async () => {
    const session = sessionRef.current;
    if (!session?.user) return;
    setLoading(true);
    setAppsError(null);
    try {
      await fetchUserApps(session.user.id, session.access_token);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fetchUserApps]);

  useEffect(() => {
    mountedRef.current = true;

    // onAuthStateChange fires INITIAL_SESSION immediately (supabase-js v2), so it is the
    // single source of truth for the initial load as well as later changes. This avoids a
    // separate getSession() call (which had no error handling -> could hang the spinner
    // forever) and the access-denied flash from rendering before app access resolved.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mountedRef.current) return;
        sessionRef.current = session;
        setUser(session?.user ?? null);
        try {
          if (session?.user) {
            // App access doesn't change on a silent token refresh, so skip the refetch then.
            if (event !== 'TOKEN_REFRESHED' && event !== 'USER_UPDATED') {
              await fetchUserApps(session.user.id, session.access_token);
            }
          } else {
            setUserApps([]);
            setAppsError(null);
          }
        } catch (err) {
          console.error('Auth state handling error:', err);
          setUserApps([]);
          setAppsError('Could not check your access. Please try again.');
        } finally {
          // Always resolve loading so the UI never gets stuck on the spinner.
          if (mountedRef.current) setLoading(false);
        }
      }
    );

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [fetchUserApps]);

  const hasAppAccess = (appName) => {
    return userApps.includes(appName);
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const value = {
    user,
    userApps,
    appsError,
    retryAppAccess,
    loading,
    hasAppAccess,
    signIn,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
